import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import JobsPage from './pages/JobsPage';
import ServiceManagementPage from './pages/ServiceManagementPage';
import PaymentsPage from './pages/PaymentsPage';
import DisputesPage from './pages/DisputesPage';
import VerificationsPage from './pages/VerificationsPage';
import SettingsPage from './pages/SettingsPage';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import ReportsPage from './pages/ReportsPage';
import NotificationsPage from './pages/NotificationsPage';
import BadgesPage from './pages/BadgesPage';
import BugReportsPage from './pages/BugReportsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminAlertsPage from './pages/AdminAlertsPage';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuthStore } from './store/authStore';
import { SpeedInsights } from '@vercel/speed-insights/react';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2563eb',
      light: '#dbeafe',
      dark: '#1e40af',
    },
    secondary: {
      main: '#10b981',
      light: '#d1fae5',
      dark: '#047857',
    },
    background: {
      default: '#f9fafb',
      paper: '#ffffff',
    },
    error: {
      main: '#ef4444',
      light: '#fee2e2',
    },
    warning: {
      main: '#f59e0b',
      light: '#fef3c7',
    },
    success: {
      main: '#10b981',
      light: '#d1fae5',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '0.5rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '0.75rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
});

function App() {
  const { isInitialized, initialize } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Unauthorized landing page */}
          <Route
            path="/unauthorized"
            element={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 16 }}>
                <h2>Access Denied</h2>
                <p>You don't have permission to view this page.</p>
                <a href="/dashboard">Go to Dashboard</a>
              </div>
            }
          />

          <Route
            path="/*"
            element={
              <ProtectedRoute requiredRole="support">
                <Layout>
                  <Routes>
                    {/* All authenticated admin roles */}
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/users" element={<UsersPage />} />
                    <Route path="/jobs" element={<JobsPage />} />
                    <Route path="/verifications" element={<VerificationsPage />} />
                    <Route path="/disputes" element={<DisputesPage />} />
                    <Route path="/admin-alerts" element={<AdminAlertsPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/support/bugreport" element={<BugReportsPage />} />
                    <Route path="/support/bugreport/" element={<BugReportsPage />} />
                    <Route path="/admin/support/bugreport" element={<BugReportsPage />} />
                    <Route path="/admin/support/bugreport/" element={<BugReportsPage />} />
                    <Route path="/badges" element={<BadgesPage />} />
                    <Route path="/profile" element={<ProfileSettingsPage />} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />

                    {/* Moderator+ routes */}
                    <Route
                      path="/services"
                      element={
                        <ProtectedRoute requiredRole="moderator">
                          <ServiceManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <ProtectedRoute requiredRole="moderator">
                          <ReportsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin-only routes — payments, audit, admin user management, settings */}
                    <Route
                      path="/payments"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <PaymentsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/audit-logs"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AuditLogsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin-users"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminUsersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <SettingsPage />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
      <SpeedInsights />
    </ThemeProvider>
  );
}

export default App;
